/**
 * Inline styles are intentional here. This page renders when Tailwind CSS
 * may not be functional (unsupported browser), so we cannot rely on utility classes.
 */

/* eslint-disable better-tailwindcss/no-unknown-classes */
import type { CSSProperties, FC } from 'react';

import { EluveLogo } from '@eluve/components';

const fontFamily =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '16px',
  fontFamily,
  backgroundColor: '#ffffff',
  textAlign: 'center',
};

const logoStyle: CSSProperties = {
  marginBottom: '16px',
};

const headingStyle: CSSProperties = {
  fontSize: '28px',
  lineHeight: '36px',
  fontWeight: 600,
  color: '#383838',
  margin: '0 0 16px',
  textWrap: 'balance',
};

const bodyTextStyle: CSSProperties = {
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '-0.01em',
  color: '#737373',
  margin: '0 0 24px',
  maxWidth: '480px',
  textWrap: 'balance',
};

const cardsContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginBottom: '16px',
  width: '100%',
  maxWidth: '640px',
};

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '8px 8px 8px 16px',
  borderRadius: '16px',
  border: '1px solid #F0F0F0',
};

const cardLabelStyle: CSSProperties = {
  fontSize: '15px',
  fontWeight: 500,
  color: '#383838',
};

const cardLinkStyle: CSSProperties = {
  display: 'inline-block',
  height: '40px',
  padding: '10px 12px',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.01em',
  fontWeight: 500,
  fontFamily,
  color: '#ffffff',
  backgroundColor: '#3C3C3C',
  borderRadius: '8px',
  textAlign: 'center',
  textDecoration: 'none',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
  width: '50%',
  transition: 'opacity 150ms ease-in-out',
};

const continueButtonStyle: CSSProperties = {
  fontSize: '12px',
  lineHeight: '18px',
  letterSpacing: '0',
  fontWeight: 500,
  color: '#383838',
  background: 'none',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontFamily,
  height: '30px',
  padding: '6px 8px',
  transition: 'background-color 150ms ease-in-out, color 150ms ease-in-out',
};

const continueNoteStyle: CSSProperties = {
  fontSize: '12px',
  lineHeight: '18px',
  letterSpacing: '0',
  color: '#919191',
  marginTop: '2px',
};

const hoverAndResponsiveCss = `
  .unsupported-browser-logo {
    width: 48px !important;
    height: 48px !important;
  }
  .unsupported-browser-link:hover { opacity: 0.9; }
  .unsupported-browser-continue:hover {
    background-color: #F5F5F5 !important;
    color: #4D4D4D !important;
  }
  @media (min-width: 640px) {
    .unsupported-browser-cards {
      flex-direction: row !important;
      gap: 16px !important;
    }
    .unsupported-browser-card {
      flex: 1 1 0;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 24px !important;
      padding: 24px 16px 16px !important;
    }
    .unsupported-browser-link { width: 100% !important; }
  }
`;

type UnsupportedBrowserPageProps = {
  onContinue?: () => void;
};

export const UnsupportedBrowserPage: FC<UnsupportedBrowserPageProps> = ({
  onContinue,
}) => {
  return (
    <div style={containerStyle}>
      <style>{hoverAndResponsiveCss}</style>

      <div style={logoStyle}>
        <EluveLogo className="unsupported-browser-logo" />
      </div>

      <h1 style={headingStyle}>Your browser is not supported</h1>

      <p style={bodyTextStyle}>
        Eluve requires a modern browser to work correctly. Please update to one
        of the following minimum versions:
      </p>

      <div className="unsupported-browser-cards" style={cardsContainerStyle}>
        <div className="unsupported-browser-card" style={cardStyle}>
          <span style={cardLabelStyle}>Chrome 111+</span>
          <a
            className="unsupported-browser-link"
            href="https://www.google.com/chrome/"
            target="_blank"
            rel="noopener noreferrer"
            style={cardLinkStyle}
          >
            Update Chrome
          </a>
        </div>
        <div className="unsupported-browser-card" style={cardStyle}>
          <span style={cardLabelStyle}>Firefox 128+</span>
          <a
            className="unsupported-browser-link"
            href="https://www.mozilla.org/firefox/"
            target="_blank"
            rel="noopener noreferrer"
            style={cardLinkStyle}
          >
            Update Firefox
          </a>
        </div>
        <div className="unsupported-browser-card" style={cardStyle}>
          <span style={cardLabelStyle}>Safari 16.4+</span>
          <a
            className="unsupported-browser-link"
            href="https://support.apple.com/en-us/102665"
            target="_blank"
            rel="noopener noreferrer"
            style={cardLinkStyle}
          >
            Update Safari
          </a>
        </div>
      </div>

      {onContinue && (
        <div style={{ textAlign: 'center' }}>
          <button
            className="unsupported-browser-continue"
            type="button"
            style={continueButtonStyle}
            onClick={onContinue}
          >
            Continue Anyway
          </button>
          <p style={continueNoteStyle}>
            Note: some features may not work correctly.
          </p>
        </div>
      )}
    </div>
  );
};
